import { useEffect, useLayoutEffect, useRef, useState } from "react";

// underline filter tabs; statuses is the set of request.status values shown.
// `key` doubles as the URL slug under /time-off/:tab.
export const FILTER_TABS = [
  { key: "all", label: "All Requests", statuses: null },
  { key: "pending", label: "Pending", statuses: [0] },
  { key: "approved", label: "Approved", statuses: [1, 2] },
  { key: "rejected", label: "Rejected", statuses: [3] },
];

// default tab when /time-off is opened without a slug
export const DEFAULT_FILTER_TAB = FILTER_TABS[0];

// resolve a URL slug (/time-off/:tab) to a tab key; falls back to the default
export const tabKeyFromPath = (slug) =>
  (FILTER_TABS.find((t) => t.key === slug) ?? DEFAULT_FILTER_TAB).key;

// Underline filter bar with a smoothly-sliding active-tab indicator.
//
// The blue underline is rendered once and absolutely positioned; when
// `activeTab` changes we measure the target button's offset/width and
// let CSS transition both. `pendingCount` is a dependency because the
// Pending tab's width changes when its badge shows/hides.
function TimeOffTabbar({ tabs = FILTER_TABS, activeTab, onChange, pendingCount = 0 }) {
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  // skip the transition until after the first measured paint, so the
  // indicator appears in place on load instead of sliding in from x=0
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const el = tabRefs.current[activeTab];
    if (!el) return undefined;

    const measure = () => {
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();

    // re-measure when the button's size changes (font load, layout
    // shifts, window resize, badge appearing/disappearing).
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab, pendingCount]);

  useEffect(() => {
    // enable the slide transition on the next frame, after the initial
    // measured position has painted.
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="relative inline-flex justify-start items-center gap-6 ">
      {/* animated active-tab underline */}
      <div
        className={`absolute bottom-0 h-0.5 bg-[#0058E5] ease-out pointer-events-none ${
          animate ? "transition-all duration-300" : ""
        }`}
        style={{ left: indicator.left, width: indicator.width }}
      />
      {tabs.map((tab) => {
        const isActive = tab.key === activeTab;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[tab.key] = el;
            }}
            onClick={() => onChange(tab.key)}
            style={{ outline: "none" }}
            className="relative z-10 h-10 flex justify-start items-center gap-1.5 focus:outline-none focus-visible:outline-none focus:ring-0"
          >
            <div
              className={`text-center text-sm font-OpenSansBold leading-5 tracking-[0.14px] capitalize ${
                isActive ? "text-[#0058E5]" : "text-[#7C829A]"
              }`}
            >
              {tab.label}
            </div>
            {tab.key === "pending" && pendingCount > 0 && (
              <div className="min-w-5 h-6 px-2 gap-1 rounded-[20px] border border-[#C9CEE2] bg-white inline-flex justify-center items-center">
                <div className="text-center text-gray-500 text-xs font-InterBold leading-4">{pendingCount}</div>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default TimeOffTabbar;
